if (typeof SearchForm !== 'undefined') {
  const USE_CUSTOM_PREDICTIVE = window.useCustomPredictiveSearch !== undefined ? window.useCustomPredictiveSearch : document.documentElement.lang === 'he';
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances =
        document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.instanceId = 'ps-' + Math.random().toString(36).slice(2);
      this.perLoad = 16;
      this.currentPage = 1;
      this.totalPages = 1;
      this.isLoadingMore = false;
      this.products = [];
      this.loadedCount = 0;

      this.setupEventListeners();
    }
  
    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
  
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }
  
    getQuery() {
      return this.input.value.trim();
    }
  
    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
      }
  
      // Update the term asap, don't wait for the predictive search query to finish loading
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
  
      this.searchTerm = newSearchTerm;
  
      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }
  
      this.getSearchResults(this.searchTerm);
    }
  
    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }
  
    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }
  
    onFocus() {
      const currentSearchTerm = this.getQuery();
  
      if (!currentSearchTerm.length) return;
  
      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }
  
    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      })
    }
  
    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();
  
      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up')
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }
  
    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (
        event.code === 'ArrowUp' ||
        event.code === 'ArrowDown'
      ) {
        event.preventDefault();
      }
    }
  
    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector(
        "[data-predictive-search-search-for-text]"
      );
      const currentButtonText = searchForTextElement?.innerText;
      if (currentButtonText) {
        if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      }
    }
  
    switchOption(direction) {
      if (!this.getAttribute('open')) return;
  
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
  
      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(
        this.querySelectorAll("li, button.predictive-search__item")
      ).filter((element) => element.offsetParent !== null);
      let activeElementIndex = 0;
  
      if (moveUp && !selectedElement) return;
  
      let selectedElementIndex = -1;
      let i = 0;
  
      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }
  
      this.statusElement.textContent = "";
  
      if (!moveUp && selectedElement) {
        activeElementIndex =
          selectedElementIndex === allVisibleElements.length - 1
            ? 0
            : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex =
          selectedElementIndex === 0
            ? allVisibleElements.length - 1
            : selectedElementIndex - 1;
      }
  
      if (activeElementIndex === selectedElementIndex) return;
  
      const activeElement = allVisibleElements[activeElementIndex];
  
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);
  
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  
    selectOption() {
      const selectedOption = this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]'
      );
  
      if (selectedOption) selectedOption.click();
    }
  
    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      this.setLiveRegionLoadingState();
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }
      if (USE_CUSTOM_PREDICTIVE) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        this.products = [];
        this.loadedCount = 0;
        this.currentPage = 1;
        this.totalPages = 1;
        this.fetchAjaxSearch(searchTerm, 1)
          .then((data) => {
            this.products = Array.isArray(data?.products) ? data.products : [];
            this.currentPage = typeof data?.page === 'number' ? data.page : 1;
            this.totalPages = typeof data?.pages === 'number' ? data.pages : 1;
            const initialCount = Math.min(this.perLoad, this.products.length);
            const products = this.products.slice(0, initialCount);
            let html = '';
            html += '<div id="predictive-search-results" role="listbox">';
            if (products.length > 0) {
              html += '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">';
              html += '<div class="predictive-search__result-group">';
              html += '<div>';
              html += '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing"></h2>';
              html += `<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" aria-labelledby="predictive-search-products">`;
              for (let i = 0; i < products.length; i++) {
                const pr = products[i];
                const url = pr.url || (pr.handle ? `/products/${pr.handle}` : '#');
                const img = pr.image || '';
                html += `<li id="predictive-search-option-product-${this.instanceId}-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">`;
                html += `<a href="${url}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">`;
                if (img) {
                  html += `<img class="predictive-search__image" src="${img}" alt="${pr.title || ''}" width="50" height="50">`;
                }
                html += '<div class="predictive-search__item-content predictive-search__item-content--centered">';
                html += `<p class="predictive-search__item-heading h5">${pr.title || ''}</p>`;
                if (typeof pr.price === 'number') {
                  const price = pr.price.toFixed(2).replace(/\.00$/, '');
                  html += `<span class="price">${price} ${pr.currency_symbol || ''}</span>`;
                  if (typeof pr.compare_at_price === 'number' && pr.compare_at_price > pr.price) {
                    const compare = pr.compare_at_price.toFixed(2).replace(/\.00$/, '');
                    html += `<span class="text-base"><s class="text-scheme-text after_discount">${compare} ${pr.currency_symbol || ''}</s></span>`;
                  }
                }
                html += '</div>';
                html += '</a>';
                html += '</li>';
              }
              html += '</ul>';
              html += '</div>';
              html += '</div>';
              html += '</div>';
            }
            html += '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">';
            html += '<button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">';
            html += `<span data-predictive-search-search-for-text>${searchTerm}</span>`;
            html += '<span class="icon-arrow"></span>';
            html += '</button>';
            html += '</div>';
            html += '</div>';
            const totalResults = products.length;
            html += `<span class="hidden" data-predictive-search-live-region-count-value>${totalResults}</span>`;
            this.cachedResults[queryKey] = html;
            this.renderSearchResults(html);
            this.loadedCount = initialCount;
            this.attachInfiniteScroll();
          })
          .catch((error) => {
            if (error?.code === 20) {
              return;
            }
            this.close();
            throw error;
          });
        return;
      }
      fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[options][unavailable_products]=hide&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku&section_id=predictive-search`,
      { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
          this.allPredictiveSearchInstances.forEach(
            (predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            }
          );
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            return;
          }
          this.close();
          throw error;
        });
    }
  
    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
  
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }
  
    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
  
      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  
    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
  
      this.setLiveRegionResults();
      this.open();
    }
  
    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }
  
    fetchAjaxSearch(term, page = 1) {
      const url = `/search?q=${encodeURIComponent(term)}&type=product&section_id=ajax-search&page=${page}`;
      return fetch(url, { signal: this.abortController?.signal })
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const json = doc.querySelector('script[type="application/json"]');
          if (!json) return { products: [] };
          return JSON.parse(json.textContent);
        });
    }
    
    attachInfiniteScroll() {
      if (!this.predictiveSearchResults) return;
      if (this.onScrollBound) this.predictiveSearchResults.removeEventListener('scroll', this.onScrollBound);
      this.onScrollBound = this.onScroll.bind(this);
      this.predictiveSearchResults.addEventListener('scroll', this.onScrollBound, { passive: true });
    }
    
    detachInfiniteScroll() {
      if (!this.predictiveSearchResults) return;
      if (this.onScrollBound) {
        this.predictiveSearchResults.removeEventListener('scroll', this.onScrollBound);
        this.onScrollBound = null;
      }
    }
    
    onScroll() {
      if (this.isLoadingMore) return;
      const el = this.predictiveSearchResults;
      if (!el) return;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
      if (!nearBottom) return;
      if (this.loadedCount < this.products.length) {
        const nextCount = Math.min(this.loadedCount + this.perLoad, this.products.length);
        this.appendProducts(this.loadedCount, nextCount);
        this.loadedCount = nextCount;
        const live = this.querySelector('[data-predictive-search-live-region-count-value]');
        if (live) live.textContent = String(this.loadedCount);
        return;
      }
      if (this.currentPage < this.totalPages) {
        this.isLoadingMore = true;
        const nextPage = this.currentPage + 1;
        this.fetchAjaxSearch(this.searchTerm, nextPage)
          .then((data) => {
            const more = Array.isArray(data?.products) ? data.products : [];
            this.products = this.products.concat(more);
            this.currentPage = typeof data?.page === 'number' ? data.page : nextPage;
            this.totalPages = typeof data?.pages === 'number' ? data.pages : this.totalPages;
            const nextCount = Math.min(this.loadedCount + this.perLoad, this.products.length);
            this.appendProducts(this.loadedCount, nextCount);
            this.loadedCount = nextCount;
            const live = this.querySelector('[data-predictive-search-live-region-count-value]');
            if (live) live.textContent = String(this.loadedCount);
          })
          .finally(() => {
            this.isLoadingMore = false;
          });
      }
    }
    
    appendProducts(startIndex, endIndex) {
      const list = this.querySelector('#predictive-search-results-products-list');
      if (!list) return;
      for (let i = startIndex; i < endIndex; i++) {
        const pr = this.products[i];
        const url = pr.url || (pr.handle ? `/products/${pr.handle}` : '#');
        const img = pr.image || '';
        let item = '';
        item += `<li id="predictive-search-option-product-${this.instanceId}-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">`;
        item += `<a href="${url}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">`;
        if (img) {
          item += `<img class="predictive-search__image" src="${img}" alt="${pr.title || ''}" width="50" height="50">`;
        }
        item += '<div class="predictive-search__item-content predictive-search__item-content--centered">';
        item += `<p class="predictive-search__item-heading h5">${pr.title || ''}</p>`;
        if (typeof pr.price === 'number') {
          const price = pr.price.toFixed(2).replace(/\.00$/, '');
          item += `<span class="price">${price} ${pr.currency_symbol || ''}</span>`;
          if (typeof pr.compare_at_price === 'number' && pr.compare_at_price > pr.price) {
            const compare = pr.compare_at_price.toFixed(2).replace(/\.00$/, '');
            item += `<span class="text-base"><s class="text-scheme-text after_discount">${compare} ${pr.currency_symbol || ''}</s></span>`;
          }
        }
        item += '</div>';
        item += '</a>';
        item += '</li>';
        list.insertAdjacentHTML('beforeend', item);
      }
    }
  
    getResultsMaxHeight() {
      this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }
  
    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }
  
    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }
  
    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false
      this.predictiveSearchResults.removeAttribute('style');
      this.detachInfiniteScroll();
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);
  }
